import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ChevronDown, IndianRupee, Wallet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtR = (n) => { if (!n || n === 0) return '-'; const a = Math.abs(n); const s = n < 0 ? '-' : ''; if (a >= 1e7) return `${s}₹${(a/1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${s}₹${(a/1e5).toFixed(2)} L`; return `${s}₹${Math.round(a).toLocaleString('en-IN')}`; };
const fmtN = (n) => n ? n.toLocaleString('en-IN') : '0';

const RECEIPT_ALIAS = {
  'Sales Payment': 'Refund',
  'Sales Receipt': 'CRM Collection',
  'Temp Receipt': 'Sales Collection',
  'Admin Receipt': 'Reg. Payment',
};

export default function SummaryTables({ token, period, refDate }) {
  const [data, setData] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const [collOpen, setCollOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/summary-tables`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(res.data);
    } catch (e) { console.error(e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return null;

  const sales = data.sales || {};
  const coll = data.collection || {};
  const sRows = (sales.rows || []).sort((a, b) => (b.net_value || 0) - (a.net_value || 0));
  const sT = sales.totals || {};
  const cRows = (coll.rows || []).sort((a, b) => (b['Sales Receipt'] || 0) - (a['Sales Receipt'] || 0));
  const cT = coll.totals || {};
  const rTypes = coll.receipt_types || [];

  return (
    <div className="space-y-4">
      {/* Sales Summary */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm overflow-hidden" data-testid="sales-summary-table">
        <button onClick={() => setSalesOpen(!salesOpen)} className="w-full">
          <CardHeader className="pb-3 border-b border-slate-100 bg-[#0F1D32] text-white hover:bg-[#162540] transition-colors cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <IndianRupee className="h-4 w-4 text-amber-400" />
                <CardTitle className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Manrope' }}>Sales Summary</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 uppercase font-medium">{sRows.length} projects</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${salesOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </button>
        {salesOpen && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sales-summary-data">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[150px]">Project</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-blue-600">Target</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Units</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Sales Value</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-slate-600">SBA (Sq.ft)</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Avg ₹/Sq.ft</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-red-600">Cancel Cnt</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-red-600">Cancel Val</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-emerald-600">Net Count</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-emerald-600">Net Value</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-emerald-600">Net SBA</th>
                  </tr>
                </thead>
                <tbody>
                  {sRows.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-2 px-3 font-medium text-slate-800 sticky left-0 bg-inherit">{r.project}</td>
                      <td className="py-2 px-3 text-right text-blue-600 font-medium">{r.target ? fmtR(r.target) : '-'}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{r.units || '-'}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{r.value ? fmtR(r.value) : '-'}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{r.sba ? fmtN(Math.round(r.sba)) : '-'}</td>
                      <td className="py-2 px-3 text-right text-blue-600 font-medium">{r.avg_cost_sqft ? fmtR(r.avg_cost_sqft) : '-'}</td>
                      <td className="py-2 px-3 text-right text-red-600">{r.c_count || '-'}</td>
                      <td className="py-2 px-3 text-right text-red-600">{r.c_value ? fmtR(r.c_value) : '-'}</td>
                      <td className="py-2 px-3 text-right text-emerald-700 font-medium">{r.net_count || '-'}</td>
                      <td className="py-2 px-3 text-right text-emerald-700 font-medium">{r.net_value ? fmtR(r.net_value) : '-'}</td>
                      <td className="py-2 px-3 text-right text-emerald-600">{r.net_sba ? fmtN(Math.round(r.net_sba)) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0F1D32] text-white font-bold">
                    <td className="py-2.5 px-3 sticky left-0 bg-[#0F1D32]">Grand Total</td>
                    <td className="py-2.5 px-3 text-right text-blue-300">{fmtR(sT.target)}</td>
                    <td className="py-2.5 px-3 text-right">{fmtN(sT.units)}</td>
                    <td className="py-2.5 px-3 text-right">{fmtR(sT.value)}</td>
                    <td className="py-2.5 px-3 text-right">{fmtN(Math.round(sT.sba || 0))}</td>
                    <td className="py-2.5 px-3 text-right text-amber-400">{fmtR(sT.avg_cost_sqft)}</td>
                    <td className="py-2.5 px-3 text-right text-red-300">{fmtN(sT.c_count)}</td>
                    <td className="py-2.5 px-3 text-right text-red-300">{fmtR(sT.c_value)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-300">{fmtN(sT.net_count)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-300">{fmtR(sT.net_value)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-300">{fmtN(Math.round(sT.net_sba || 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Collection Summary */}
      <Card className="bg-white rounded-2xl border-slate-200/60 shadow-sm overflow-hidden" data-testid="collection-summary-table">
        <button onClick={() => setCollOpen(!collOpen)} className="w-full">
          <CardHeader className="pb-3 border-b border-slate-100 bg-[#0F1D32] text-white hover:bg-[#162540] transition-colors cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Wallet className="h-4 w-4 text-emerald-400" />
                <CardTitle className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Manrope' }}>Collection Summary</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 uppercase font-medium">{rTypes.length} receipt types</span>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CardHeader>
        </button>
        {collOpen && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="collection-summary-data">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[150px]">Project</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-blue-600 min-w-[100px]">Target</th>
                    {rTypes.map(rt => <th key={rt} className="text-right py-2.5 px-3 font-semibold text-slate-600 min-w-[120px]">{RECEIPT_ALIAS[rt] || rt}</th>)}
                    <th className="text-right py-2.5 px-3 font-semibold text-emerald-700 min-w-[120px]">Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cRows.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="py-2 px-3 font-medium text-slate-800 sticky left-0 bg-inherit">{r.project}</td>
                      <td className="py-2 px-3 text-right text-blue-600 font-medium">{r.target ? fmtR(r.target) : '-'}</td>
                      {rTypes.map(rt => <td key={rt} className="py-2 px-3 text-right text-slate-700">{r[rt] ? fmtR(r[rt]) : '-'}</td>)}
                      <td className="py-2 px-3 text-right text-emerald-700 font-semibold">{r.grand_total ? fmtR(r.grand_total) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0F1D32] text-white font-bold">
                    <td className="py-2.5 px-3 sticky left-0 bg-[#0F1D32]">Grand Total</td>
                    <td className="py-2.5 px-3 text-right text-blue-300">{fmtR(cT.target)}</td>
                    {rTypes.map(rt => <td key={rt} className="py-2.5 px-3 text-right">{fmtR(cT[rt])}</td>)}
                    <td className="py-2.5 px-3 text-right text-emerald-300">{fmtR(cT.grand_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
